/**
 * Collection Letter Markup Page
 * Main page for collection letter analysis and review.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FileText, Upload, Search, AlertTriangle, CheckCircle, 
  XCircle, Eye, Download, Trash2, RefreshCw, ChevronRight,
  Scale, FileWarning, Loader2, ArrowLeft, ZoomIn, ZoomOut,
  ChevronLeft, Filter, MoreVertical, Edit, Merge, Plus, MapPin, Copy, Send, Printer, Mail, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

import * as collectionMarkupService from '../../services/collectionMarkupService';
import DocumentAnnotationOverlay from '../../components/collection-markup/DocumentAnnotationOverlay';
import InteractiveAnnotationOverlay from '../../components/collection-markup/InteractiveAnnotationOverlay';
import MarkupEditorToolbar, { TOOL_TYPES } from '../../components/collection-markup/MarkupEditorToolbar';
import DeepAnalysisOverlay from '../../components/collection-markup/DeepAnalysisOverlay';
import FloatingToolbox from '../../components/collection-markup/FloatingToolbox';

// Severity badge colors (for findings panel, not document markup)
const SEVERITY_COLORS = {
  high: 'bg-red-500/10 text-red-600 border-red-500/30',
  medium: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  low: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  info: 'bg-slate-500/10 text-slate-600 border-slate-500/30'
};

// Status colors
const STATUS_COLORS = {
  auto_flagged: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30',
  confirmed: 'bg-green-500/10 text-green-700 border-green-500/30',
  dismissed: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
  edited: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  merged: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
};

// Category labels
const CATEGORY_LABELS = {
  disclosure: 'Disclosure',
  validation_rights: 'Validation Rights',
  overshadowing: 'Overshadowing',
  amount_info: 'Amount/Balance',
  creditor_id: 'Creditor ID',
  response_form: 'Response Form',
  contact_methods: 'Contact Methods',
  formatting: 'Formatting',
  misleading_language: 'Misleading Language'
};

const CollectionLetterMarkupPage = () => {
  // State
  const [view, setView] = useState('list'); // list, upload, review
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [rawFindings, setRawFindings] = useState([]);
  const [normalizedFindings, setNormalizedFindings] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewDimensions, setPreviewDimensions] = useState({ width: 0, height: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [annotationOverlay, setAnnotationOverlay] = useState(null);
  
  // Refs
  const documentContainerRef = useRef(null);
  
  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFinding, setEditingFinding] = useState(null);
  
  // Send state
  const [sendState, setSendState] = useState({
    step: 'prepare', // prepare, validate, review, sending, complete
    snapshotId: null,
    exportId: null,
    recipient: null,
    sendId: null,
    error: null,
    addressValidated: false,
    postgridResult: null
  });
  
  // Applied Snapshot & Dirty State
  const [isDirty, setIsDirty] = useState(false);
  const [latestSnapshot, setLatestSnapshot] = useState(null);
  const [showReviewChangesModal, setShowReviewChangesModal] = useState(false);
  const [changesSummary, setChangesSummary] = useState({ added: 0, modified: 0, deleted: 0, moved: 0 });
  const [isApplying, setIsApplying] = useState(false);
  const [originalAnnotationsRef, setOriginalAnnotationsRef] = useState([]);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Manual Markup Editor state
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTool, setActiveTool] = useState(TOOL_TYPES.SELECT);
  const [editedAnnotations, setEditedAnnotations] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [originalAIAnnotations, setOriginalAIAnnotations] = useState([]);
  
  // Deep Analysis state - controls when markup can be shown
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisStage, setAnalysisStage] = useState('starting');
  
  // Visible findings filter - limit on-page markup for readability
  // but always show ALL findings in the findings panel
  const [showOnlyHighConfidence, setShowOnlyHighConfidence] = useState(true);
  
  // Highlight state for "Click to locate" feature
  const [highlightedFindingId, setHighlightedFindingId] = useState(null);

  // Load documents and permissions on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [docsResult, permResult] = await Promise.all([
        collectionMarkupService.listDocuments({ limit: 50 }),
        collectionMarkupService.getPermissions()
      ]);
      
      setDocuments(docsResult.documents || []);
      setPermissions(permResult.permissions);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load document for review
  const loadDocument = async (documentId) => {
    console.log('Loading document:', documentId);
    setLoading(true);
    try {
      const result = await collectionMarkupService.getDocument(documentId);
      console.log('Document loaded:', result);
      setSelectedDocument(result.document);
      setRawFindings(result.raw_findings || []);
      setNormalizedFindings(result.normalized_findings || []);
      
      // Load preview
      if (result.document?.status === 'complete') {
        await loadPreview(documentId, 1);
        // Mark analysis as complete so markup can be shown
        setAnalysisComplete(true);
      } else {
        setAnalysisComplete(false);
      }
      
      setView('review');
      console.log('View set to review');
    } catch (error) {
      console.error('Failed to load document:', error);
      toast.error('Failed to load document: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load preview image
  const loadPreview = async (documentId, page) => {
    try {
      const result = await collectionMarkupService.getDocumentPreview(documentId, page);
      setPreviewImage(result.image_base64);
      setCurrentPage(page);
      setTotalPages(result.total_pages || 1);
    } catch (error) {
      console.error('Failed to load preview:', error);
    }
  };

  // Handle file upload
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    
    setUploading(true);
    try {
      const result = await collectionMarkupService.uploadDocument(uploadFile);
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      setUploadFile(null);
      
      // Start analysis automatically
      await startAnalysis(result.document_id);
      
      // Refresh list
      loadInitialData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Start analysis - Deep Analysis Mode
  // Takes time deliberately for quality over speed
  const startAnalysis = async (documentId) => {
    setAnalyzing(true);
    setAnalysisComplete(false);
    setAnalysisStage('starting');
    setAnalysisProgress({ stage: 'extracting', progress: 0 });
    
    try {
      const startResult = await collectionMarkupService.startAnalysis(documentId);
      
      if (startResult.status === 'already_complete') {
        toast.info('Document already analyzed');
        setAnalysisComplete(true);
        await loadDocument(documentId);
        return;
      }
      
      // Poll for completion with detailed stage updates
      await collectionMarkupService.pollAnalysisUntilComplete(
        startResult.job_id,
        (status) => {
          // Map backend stages to UI stages for deliberate flow
          const stageMap = {
            'starting': 'extracting',
            'extracting': 'extracting',
            'ocr': 'mapping',
            'detecting_issues': 'dispute_rights',
            'ai_analysis': 'validation',
            'normalizing': 'comparing',
            'building_annotations': 'precision',
            'finalizing': 'finalizing',
            'complete': 'finalizing'
          };
          const uiStage = stageMap[status.stage] || status.stage;
          setAnalysisStage(uiStage);
          setAnalysisProgress({
            stage: uiStage,
            progress: status.progress
          });
        },
        // Allow longer timeout for deep analysis (120 seconds)
        120000
      );
      
      toast.success('Deep analysis complete');
      setAnalysisComplete(true);
      await loadDocument(documentId);
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error(error.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
      setAnalysisProgress(null);
    }
  };

  // Update finding status
  const handleFindingAction = async (finding, action) => {
    const findingId = finding.finding_id || finding.normalized_id;
    
    let updates = {};
    switch (action) {
      case 'confirm':
        updates = { reviewer_status: 'confirmed' };
        break;
      case 'dismiss':
        updates = { reviewer_status: 'dismissed' };
        break;
      case 'edit':
        setEditingFinding(finding);
        setShowEditModal(true);
        return;
      default:
        return;
    }
    
    try {
      await collectionMarkupService.updateFinding(findingId, updates);
      toast.success(`Finding ${action}ed`);
      
      // Refresh findings
      if (selectedDocument) {
        await loadDocument(selectedDocument.document_id);
      }
    } catch (error) {
      console.error('Failed to update finding:', error);
      toast.error('Failed to update finding');
    }
  };

  // Save edited finding
  const handleSaveEdit = async () => {
    if (!editingFinding) return;
    
    const findingId = editingFinding.finding_id || editingFinding.normalized_id;
    
    try {
      await collectionMarkupService.updateFinding(findingId, {
        short_label: editingFinding.short_label,
        explanation: editingFinding.explanation,
        severity: editingFinding.severity,
        reviewer_notes: editingFinding.reviewer_notes
      });
      
      toast.success('Finding updated');
      setShowEditModal(false);
      setEditingFinding(null);
      
      if (selectedDocument) {
        await loadDocument(selectedDocument.document_id);
      }
    } catch (error) {
      console.error('Failed to save edit:', error);
      toast.error('Failed to save changes');
    }
  };

  // Export document
  const handleExport = async (format) => {
    if (!selectedDocument) return;
    
    try {
      const result = await collectionMarkupService.exportDocument(
        selectedDocument.document_id,
        format
      );
      
      if (format === 'pdf' && result.data_base64) {
        // Download PDF
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${result.data_base64}`;
        link.download = result.filename || 'annotated_document.pdf';
        link.click();
        toast.success('PDF downloaded');
      } else if (format === 'json') {
        // Download JSON
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'analysis_results.json';
        link.click();
        toast.success('JSON downloaded');
      } else if (format === 'summary') {
        // Show summary in modal or download
        const blob = new Blob([result.data], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'issue_summary.txt';
        link.click();
        toast.success('Summary downloaded');
      }
      
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.message || 'Export failed');
    }
  };

  // ==================== PRINT HANDLER ====================
  // PRINT ARTIFACT RULE: Print must use the same rendered reviewed artifact pipeline as Export
  const handlePrint = async () => {
    if (!canExportPrintSend) {
      toast.error('Apply changes before printing');
      return;
    }
    
    try {
      toast.info('Preparing print preview...');
      
      // Generate the reviewed export artifact (same as Export)
      const result = await collectionMarkupService.exportDocument(
        selectedDocument.document_id,
        'pdf'
      );
      
      if (result.data_base64) {
        // Create a blob from the PDF data
        const pdfBlob = await fetch(`data:application/pdf;base64,${result.data_base64}`)
          .then(res => res.blob());
        
        // Create an object URL for the blob
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Open in a new window for printing
        const printWindow = window.open(pdfUrl, '_blank');
        
        if (printWindow) {
          // Wait for the PDF to load, then trigger print
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 500);
          };
          toast.success('Print dialog opened');
        } else {
          // Fallback: download the PDF instead
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.download = `${selectedDocument.original_filename.replace('.pdf', '')}_print.pdf`;
          link.click();
          toast.info('PDF downloaded - please open and print manually');
        }
        
        // Clean up after a delay
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      } else {
        toast.error('Failed to generate print preview');
      }
    } catch (error) {
      console.error('Print failed:', error);
      toast.error(error.message || 'Print failed');
    }
  };

  // ==================== SEND MODAL HANDLERS (POSTAL MAIL) ====================
  
  // Open send modal - extract mailing address
  const handleOpenSendModal = async () => {
    if (!canExportPrintSend) {
      toast.error('Apply changes before sending');
      return;
    }
    
    setSendState(prev => ({ ...prev, step: 'prepare', error: null }));
    setShowSendModal(true);
    
    try {
      // Extract mailing address from letter
      const addressResult = await collectionMarkupService.extractMailingAddress(selectedDocument.document_id);
      
      setSendState(prev => ({
        ...prev,
        recipient: {
          ...addressResult.address,
          extracted_from_letter: true,
          manually_edited: false
        },
        snapshotId: latestSnapshot?.snapshot_id,
        step: 'prepare'
      }));
    } catch (error) {
      console.error('Failed to extract address:', error);
      // Default to empty address - user can fill in
      setSendState(prev => ({
        ...prev,
        recipient: {
          collector_name: '',
          company_name: '',
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'US',
          extracted_from_letter: false,
          manually_edited: false
        },
        snapshotId: latestSnapshot?.snapshot_id,
        step: 'prepare'
      }));
    }
  };
  
  // Validate address via PostGrid
  const handleValidateAddress = async () => {
    setSendState(prev => ({ ...prev, step: 'validating', error: null }));
    
    try {
      const result = await collectionMarkupService.validateAddress({
        address_line1: sendState.recipient?.address_line1 || '',
        address_line2: sendState.recipient?.address_line2 || '',
        city: sendState.recipient?.city || '',
        state: sendState.recipient?.state || '',
        postal_code: sendState.recipient?.postal_code || '',
        country: sendState.recipient?.country || 'US'
      });
      
      if (result.success && result.validated) {
        // Use normalized address
        setSendState(prev => ({
          ...prev,
          recipient: {
            ...prev.recipient,
            ...result.normalized,
            address_validated: true
          },
          addressValidated: true,
          postgridResult: result,
          step: 'review'
        }));
      } else {
        setSendState(prev => ({
          ...prev,
          postgridResult: result,
          step: 'review',
          error: result.error || 'Address validation failed - please verify manually'
        }));
      }
    } catch (error) {
      console.error('Address validation failed:', error);
      // Proceed anyway - validation is optional
      setSendState(prev => ({
        ...prev,
        step: 'review',
        error: 'Address validation unavailable - please verify address'
      }));
    }
  };
  
  // Execute postal mail send via PostGrid
  const handleExecutePostalSend = async () => {
    // Validate required fields
    if (!sendState.recipient?.address_line1 || !sendState.recipient?.city || !sendState.recipient?.state) {
      toast.error('Complete mailing address is required');
      return;
    }
    
    setSendState(prev => ({ ...prev, step: 'sending', error: null }));
    
    try {
      // Create postal send draft
      const draftResult = await collectionMarkupService.createPostalSend(
        selectedDocument.document_id,
        sendState.snapshotId || latestSnapshot?.snapshot_id,
        {
          ...sendState.recipient,
          manually_edited: sendState.recipient?.manually_edited || false
        }
      );
      
      if (!draftResult.success) {
        throw new Error('Failed to create send draft');
      }
      
      // Get sender info (would come from agency settings in production)
      const senderInfo = {
        first_name: 'Your',
        last_name: 'Agency',
        company_name: 'Your Company',
        address_line1: '123 Main St',
        city: 'Your City',
        state: 'CA',
        postal_code: '90210',
        country: 'US'
      };
      
      // Execute the postal send
      const sendResult = await collectionMarkupService.executePostalSend(
        draftResult.send_record.send_id,
        senderInfo
      );
      
      if (sendResult.success) {
        setSendState(prev => ({
          ...prev,
          sendId: draftResult.send_record.send_id,
          postgridResult: sendResult,
          step: 'complete'
        }));
        toast.success(sendResult.demo_mode ? 'Mail job created (demo mode)' : 'Mail job submitted');
      } else {
        throw new Error(sendResult.error || 'Mail submission failed');
      }
    } catch (error) {
      console.error('Postal send failed:', error);
      setSendState(prev => ({
        ...prev,
        error: error.message,
        step: 'review'
      }));
      toast.error(error.message || 'Mail submission failed');
    }
  };
  
  // Close send modal and reset state
  const handleCloseSendModal = () => {
    setShowSendModal(false);
    setSendState({
      step: 'prepare',
      snapshotId: null,
      exportId: null,
      recipient: null,
      sendId: null,
      error: null,
      addressValidated: false,
      postgridResult: null
    });
  };

  // Delete document
  const handleDelete = async (documentId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await collectionMarkupService.deleteDocument(documentId);
      toast.success('Document deleted');
      
      if (selectedDocument?.document_id === documentId) {
        setView('list');
        setSelectedDocument(null);
      }
      
      loadInitialData();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete document');
    }
  };

  // Filter findings
  // IMPORTANT: For ANNOTATION RENDERING, use raw findings (they have exact coordinates)
  const getFilteredFindings = useCallback(() => {
    // Raw findings have coordinates from text block matching
    // Use them for annotation rendering
    let findings = rawFindings.length > 0 ? [...rawFindings] : [...normalizedFindings];
    
    if (filterStatus !== 'all') {
      findings = findings.filter(f => f.reviewer_status === filterStatus);
    }
    
    if (filterCategory !== 'all') {
      findings = findings.filter(f => 
        (f.issue_type || f.normalized_issue_type) === filterCategory
      );
    }
    
    return findings;
  }, [normalizedFindings, rawFindings, filterStatus, filterCategory]);

  // Generic category labels that should NOT be rendered as document annotations
  const SUPPRESSED_CATEGORY_LABELS = new Set([
    'Debt Collector Disclosure',
    'Validation Notice / Dispute Rights',
    'Creditor Identification',
    'Amount / Debt Information',
    'Formatting / Structural Risks',
    'Response Form / Checkbox Issue',
    'Contact / Response Methods',
    'Misleading Language',
    'Overshadowing'
  ]);

  // Check if a label is a generic category header (should be suppressed)
  const isGenericCategoryLabel = (label) => {
    if (!label) return true;
    const normalized = label.trim();
    return SUPPRESSED_CATEGORY_LABELS.has(normalized) || 
           normalized.length < 15 ||  // Too short to be specific
           !normalized.toLowerCase().includes('possible') && 
           !normalized.toLowerCase().includes('potential') &&
           !normalized.toLowerCase().includes('may') &&
           !normalized.toLowerCase().includes('unclear') &&
           !normalized.toLowerCase().includes('misleading') &&
           !normalized.toLowerCase().includes('failure');
  };

  // Format label with law reference inline
  const formatAnnotationLabel = (finding) => {
    const label = finding.short_label || finding.normalized_label || '';
    const lawRefs = finding.law_references || finding.merged_law_references || [];
    
    // Skip generic category labels
    if (isGenericCategoryLabel(label)) {
      return null;
    }
    
    // Add "Possible" prefix if not already present
    let formattedLabel = label;
    if (!label.toLowerCase().startsWith('possible') && 
        !label.toLowerCase().startsWith('potential') &&
        !label.toLowerCase().startsWith('may')) {
      formattedLabel = `Possible ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
    }
    
    // Append law reference if available
    if (lawRefs.length > 0) {
      const lawTag = lawRefs[0].replace('FDCPA ', '§').replace('1692', '1692');
      if (!formattedLabel.includes('§') && !formattedLabel.includes('FDCPA')) {
        formattedLabel = `${formattedLabel} — ${lawTag}`;
      }
    }
    
    return formattedLabel;
  };

  // Check if two rectangles overlap
  const rectanglesOverlap = (r1, r2, margin = 10) => {
    return !(r1.x + r1.width + margin < r2.x ||
             r2.x + r2.width + margin < r1.x ||
             r1.y + r1.height + margin < r2.y ||
             r2.y + r2.height + margin < r1.y);
  };

  // Build annotations from findings for the overlay component
  // This filters for display on the document - prioritizes high confidence
  // The full list is always shown in the findings panel
  const buildAnnotationsFromFindings = useCallback(() => {
    let filteredFindings = getFilteredFindings();
    
    // If Priority View is enabled, filter to show only best-anchored findings
    // This reduces visual clutter while keeping ALL findings in the panel
    if (showOnlyHighConfidence && !isEditMode) {
      filteredFindings = filteredFindings.filter(f => {
        const confidence = f.confidence_score || f.normalized_confidence || 0.5;
        const precision = f.precision_score || 0.5;
        // Show only high confidence OR high precision findings on page
        return confidence >= 0.75 || precision >= 0.8 || f.severity === 'high';
      });
      // Limit to top 5-7 findings per page to avoid clutter
      const byPage = {};
      filteredFindings.forEach(f => {
        const page = f.page_number || 1;
        if (!byPage[page]) byPage[page] = [];
        byPage[page].push(f);
      });
      filteredFindings = Object.values(byPage).flatMap(pageFindings => 
        pageFindings.slice(0, 7)
      );
    }
    
    // PRECISION RULE: Filter out findings without valid coordinates
    // Findings with no coordinates (unanchored) should not render visible boxes
    filteredFindings = filteredFindings.filter(f => {
      const coords = f.coordinates || {};
      // Must have valid x, y coordinates (not default/guessed)
      const hasValidCoords = coords.x !== undefined && coords.y !== undefined && 
                             (coords.width > 0 || coords.height > 0);
      // Precision filter - allow if precision >= 0.3 (relaxed to show existing data)
      const hasPrecision = (f.precision_score || 0.5) >= 0.3;
      return hasValidCoords && hasPrecision;
    });
    
    // First pass: create candidate annotations
    const candidates = filteredFindings.map((finding, idx) => {
      const coords = finding.coordinates || {};
      const pageNum = finding.page_number || 1;
      
      // Format label with law theory inline
      const formattedLabel = formatAnnotationLabel(finding);
      
      // Skip if label is generic or null
      if (!formattedLabel) {
        return null;
      }
      
      // Box coordinates - use EXACT coordinates from backend
      // Do NOT add arbitrary offsets - trust the backend geometry
      const boxX = coords.x || 50;
      const boxY = coords.y || 100;
      const boxWidth = Math.max(coords.width || 200, 42);
      const boxHeight = Math.max(coords.height || 20, 18);
      
      return {
        id: finding.finding_id || finding.normalized_id || `finding-${idx}`,
        page_number: pageNum,
        target_region: {
          x: boxX,
          y: boxY,
          width: boxWidth,
          height: boxHeight
        },
        original_label: finding.short_label || finding.normalized_label,
        label_text: formattedLabel,
        severity: finding.severity || finding.normalized_severity || 'medium',
        law_references: finding.law_references || finding.merged_law_references || [],
        reviewer_status: finding.reviewer_status || 'auto_flagged',
        confidence_score: finding.confidence_score || finding.normalized_confidence || 0.75,
        precision_score: finding.precision_score || 0.85,
        text_association_score: finding.text_association_score || 0.85,
        annotation_scope: finding.annotation_scope || 'sentence',
        supporting_snippet: finding.detected_text_snippet || ''
      };
    }).filter(Boolean); // Remove nulls
    
    // Second pass: deduplicate overlapping rectangles and position labels
    const usedRegions = [];
    const usedLabelPositions = [];
    const finalAnnotations = [];
    
    for (const annotation of candidates) {
      const region = annotation.target_region;
      
      // Check if this rectangle overlaps with an existing one
      const overlappingRegion = usedRegions.find(r => 
        r.page === annotation.page_number && 
        rectanglesOverlap(r, region, 5)
      );
      
      if (overlappingRegion) {
        // Same anchor - skip rendering duplicate rectangle
        // Only keep if this is a distinctly different theory
        const existingAnnotation = finalAnnotations.find(a => 
          a.page_number === annotation.page_number &&
          rectanglesOverlap(a.target_region, region, 5)
        );
        
        if (existingAnnotation) {
          // Check if labels are similar
          const existingLabelLower = existingAnnotation.label_text.toLowerCase();
          const newLabelLower = annotation.label_text.toLowerCase();
          
          // If very similar, skip
          if (existingLabelLower.includes(newLabelLower.split(' — ')[0].slice(0, 20)) ||
              newLabelLower.includes(existingLabelLower.split(' — ')[0].slice(0, 20))) {
            continue;
          }
        }
      }
      
      // Calculate label position avoiding collisions
      let labelX = region.x + region.width + 15;
      let labelY = region.y + 5;
      
      // Check for label collisions and adjust
      let attempts = 0;
      while (attempts < 10) {
        const labelRect = { x: labelX, y: labelY, width: 220, height: 50 };
        const collision = usedLabelPositions.find(lp => 
          lp.page === annotation.page_number &&
          rectanglesOverlap(lp, labelRect, 5)
        );
        
        if (!collision) break;
        
        // Stagger label position
        labelY += 60;
        attempts++;
      }
      
      // Arrow from label to box
      const arrowFromX = labelX - 5;
      const arrowFromY = labelY + 15;
      const arrowToX = region.x + region.width + 3;
      const arrowToY = region.y + (region.height / 2);
      
      // Add to used regions and positions
      usedRegions.push({ ...region, page: annotation.page_number });
      usedLabelPositions.push({ 
        x: labelX, 
        y: labelY, 
        width: 220, 
        height: 50, 
        page: annotation.page_number 
      });
      
      finalAnnotations.push({
        ...annotation,
        label_position: { x: labelX, y: labelY },
        arrow_from: { x: arrowFromX, y: arrowFromY },
        arrow_to: { x: arrowToX, y: arrowToY }
      });
    }
    
    return finalAnnotations;
  }, [getFilteredFindings, showOnlyHighConfidence, isEditMode]);

  // Handle annotation click from overlay
  const handleAnnotationClick = useCallback((annotation) => {
    // Find the corresponding finding
    const finding = [...rawFindings, ...normalizedFindings].find(
      f => (f.finding_id === annotation.id) || (f.normalized_id === annotation.id)
    );
    
    if (finding) {
      setSelectedFinding(finding);
      
      // Scroll finding card into view
      const cardElement = document.querySelector(`[data-finding-id="${annotation.id}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [rawFindings, normalizedFindings]);

  // Initialize edited annotations when switching to edit mode or loading new document
  useEffect(() => {
    if (selectedDocument && rawFindings.length > 0) {
      const aiAnnotations = buildAnnotationsFromFindings().map(ann => ({
        ...ann,
        source: 'ai',
        original_ai_position: { ...ann.target_region },
        original_ai_label: ann.label_text,
        original_ai_law_tags: [...(ann.law_references || [])],
        original_ai_snippet: ann.supporting_snippet
      }));
      setOriginalAIAnnotations(aiAnnotations);
      setEditedAnnotations(aiAnnotations);
      setUndoStack([]);
      setRedoStack([]);
    }
  }, [selectedDocument, rawFindings.length]);

  // Get current annotations (edited or AI-generated)
  const getCurrentAnnotations = useCallback(() => {
    if (isEditMode && editedAnnotations.length > 0) {
      return editedAnnotations;
    }
    return buildAnnotationsFromFindings();
  }, [isEditMode, editedAnnotations, buildAnnotationsFromFindings]);

  // Handle annotation selection in edit mode
  const handleAnnotationSelect = useCallback((annotation) => {
    if (annotation) {
      setSelectedFinding(annotation);
      // Switch to select tool when clicking an annotation
      setActiveTool(TOOL_TYPES.SELECT);
    } else {
      setSelectedFinding(null);
    }
  }, []);

  // Handle annotation update (move, resize, relabel)
  const handleAnnotationUpdate = useCallback((updatedAnnotation) => {
    // Save current state to undo stack
    setUndoStack(prev => [...prev, [...editedAnnotations]]);
    setRedoStack([]); // Clear redo stack on new action
    
    setEditedAnnotations(prev => 
      prev.map(ann => 
        ann.id === updatedAnnotation.id ? { ...ann, ...updatedAnnotation } : ann
      )
    );
  }, [editedAnnotations]);

  // Handle creating new manual annotation
  const handleCreateAnnotation = useCallback((newAnnotation) => {
    // Save current state to undo stack
    setUndoStack(prev => [...prev, [...editedAnnotations]]);
    setRedoStack([]);
    
    setEditedAnnotations(prev => [...prev, newAnnotation]);
    setSelectedFinding(newAnnotation);
    toast.success('Annotation added. Click to edit.');
  }, [editedAnnotations]);

  // Handle deleting selected annotation
  const handleDeleteSelected = useCallback(() => {
    if (!selectedFinding) return;
    
    // Save current state to undo stack
    setUndoStack(prev => [...prev, [...editedAnnotations]]);
    setRedoStack([]);
    
    setEditedAnnotations(prev => 
      prev.filter(ann => ann.id !== selectedFinding.id)
    );
    setSelectedFinding(null);
    toast.success('Annotation deleted');
  }, [selectedFinding, editedAnnotations]);

  // Handle deleting a specific annotation (for delete tool)
  const handleDeleteAnnotation = useCallback((annotation) => {
    if (!annotation) return;
    
    // Save current state to undo stack
    setUndoStack(prev => [...prev, [...editedAnnotations]]);
    setRedoStack([]);
    
    setEditedAnnotations(prev => 
      prev.filter(ann => ann.id !== annotation.id)
    );
    if (selectedFinding?.id === annotation.id) {
      setSelectedFinding(null);
    }
    toast.success('Annotation deleted');
  }, [editedAnnotations, selectedFinding]);

  // Undo action
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    
    const previousState = undoStack[undoStack.length - 1];
    setRedoStack(prev => [...prev, [...editedAnnotations]]);
    setEditedAnnotations(previousState);
    setUndoStack(prev => prev.slice(0, -1));
  }, [undoStack, editedAnnotations]);

  // Redo action
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    
    const nextState = redoStack[redoStack.length - 1];
    setUndoStack(prev => [...prev, [...editedAnnotations]]);
    setEditedAnnotations(nextState);
    setRedoStack(prev => prev.slice(0, -1));
  }, [redoStack, editedAnnotations]);

  // Reset to original AI suggestions
  const handleResetToAI = useCallback(() => {
    if (!confirm('Reset all annotations to original AI suggestions? This will discard all manual edits.')) return;
    
    setUndoStack(prev => [...prev, [...editedAnnotations]]);
    setRedoStack([]);
    setEditedAnnotations([...originalAIAnnotations]);
    toast.success('Reset to AI suggestions');
  }, [editedAnnotations, originalAIAnnotations]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isEditMode) return;
      
      // Delete selected
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFinding) {
        e.preventDefault();
        handleDeleteSelected();
      }
      
      // Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      
      // Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
      
      // Tool shortcuts
      if (e.key === 'v' || e.key === 'V') setActiveTool(TOOL_TYPES.SELECT);
      if (e.key === 'r' || e.key === 'R') setActiveTool(TOOL_TYPES.RECTANGLE);
      if (e.key === 'd' || e.key === 'D') setActiveTool(TOOL_TYPES.DASHED_RECTANGLE);
      if (e.key === 'a' || e.key === 'A') setActiveTool(TOOL_TYPES.ARROW);
      if (e.key === 't' || e.key === 'T') setActiveTool(TOOL_TYPES.TEXT_NOTE);
      
      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedFinding(null);
        setActiveTool(TOOL_TYPES.SELECT);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, selectedFinding, handleDeleteSelected, handleUndo, handleRedo]);

  // ==================== DIRTY STATE & SNAPSHOT TRACKING ====================
  
  // Track dirty state when annotations change in edit mode
  useEffect(() => {
    if (!isEditMode || !selectedDocument) return;
    
    // Compare current editedAnnotations with originalAnnotationsRef
    const current = JSON.stringify(editedAnnotations);
    const original = JSON.stringify(originalAnnotationsRef);
    
    if (current !== original && editedAnnotations.length > 0) {
      setIsDirty(true);
      
      // Calculate changes summary
      const added = editedAnnotations.filter(a => 
        !originalAnnotationsRef.some(o => o.id === a.id)
      ).length;
      const deleted = originalAnnotationsRef.filter(o => 
        !editedAnnotations.some(a => a.id === o.id)
      ).length;
      const modified = editedAnnotations.filter(a => {
        const original = originalAnnotationsRef.find(o => o.id === a.id);
        return original && JSON.stringify(a) !== JSON.stringify(original);
      }).length;
      
      setChangesSummary({ added, deleted, modified, moved: 0 });
    }
  }, [editedAnnotations, originalAnnotationsRef, isEditMode, selectedDocument]);
  
  // Store original annotations when entering edit mode
  useEffect(() => {
    if (isEditMode && editedAnnotations.length > 0 && originalAnnotationsRef.length === 0) {
      setOriginalAnnotationsRef([...editedAnnotations]);
    }
  }, [isEditMode, editedAnnotations, originalAnnotationsRef]);
  
  // Load latest snapshot when document is selected
  useEffect(() => {
    const loadSnapshot = async () => {
      if (!selectedDocument) return;
      
      try {
        const result = await collectionMarkupService.getLatestSnapshot(selectedDocument.document_id);
        if (result.success && result.snapshot) {
          setLatestSnapshot(result.snapshot);
        }
      } catch (error) {
        console.error('Failed to load snapshot:', error);
      }
    };
    
    loadSnapshot();
  }, [selectedDocument]);
  
  // Unsaved exit warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unapplied markup edits. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // ==================== APPLY CHANGES HANDLERS ====================
  
  // Open Review Changes modal
  const handleOpenReviewChanges = () => {
    setShowReviewChangesModal(true);
  };
  
  // Apply changes and create snapshot
  const handleApplyChanges = async () => {
    if (!selectedDocument || !isDirty) return;
    
    setIsApplying(true);
    
    try {
      const annotations = getCurrentAnnotations();
      
      const result = await collectionMarkupService.applyChanges(
        selectedDocument.document_id,
        annotations,
        changesSummary
      );
      
      if (result.success) {
        setLatestSnapshot(result.snapshot);
        setIsDirty(false);
        setOriginalAnnotationsRef([...editedAnnotations]);
        setShowReviewChangesModal(false);
        toast.success('Changes applied successfully');
      }
    } catch (error) {
      console.error('Apply changes failed:', error);
      toast.error(error.message || 'Failed to apply changes');
    } finally {
      setIsApplying(false);
    }
  };
  
  // Discard unsaved changes
  const handleDiscardChanges = () => {
    setEditedAnnotations([...originalAnnotationsRef]);
    setIsDirty(false);
    setShowReviewChangesModal(false);
    toast.success('Changes discarded');
  };
  
  // Check if Export/Print/Send should be enabled
  const canExportPrintSend = !isDirty && (latestSnapshot !== null || !isEditMode);

  // Render document list
  const renderDocumentList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Collection Letter Markup</h1>
          <p className="text-slate-600 mt-1">
            AI-assisted analysis of debt collection letters for potential compliance issues
          </p>
        </div>
        <Button 
          onClick={() => setShowUploadModal(true)}
          data-testid="upload-document-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Letter
        </Button>
      </div>

      {/* Tier notice for Auto Starter */}
      {permissions && !permissions.can_edit_findings && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Limited Access</p>
            <p className="text-sm text-amber-700 mt-1">
              Your current plan includes basic collection letter review. Upgrade to Pro or Agency for 
              advanced editing, merge/split, and PDF exports.
            </p>
          </div>
        </div>
      )}

      {/* Document list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-900">No documents yet</h3>
            <p className="text-slate-500 mt-1 mb-4">
              Upload a collection letter to get started with AI-assisted compliance review
            </p>
            <Button onClick={() => setShowUploadModal(true)} data-testid="empty-upload-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Your First Letter
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {documents.map((doc) => (
            <Card 
              key={doc.document_id} 
              className="hover:border-slate-300 transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                loadDocument(doc.document_id);
              }}
              data-testid={`document-card-${doc.document_id}`}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    doc.status === 'complete' ? 'bg-green-100' : 
                    doc.status === 'failed' ? 'bg-red-100' : 'bg-slate-100'
                  }`}>
                    {doc.status === 'complete' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : doc.status === 'failed' ? (
                      <XCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <FileText className="w-5 h-5 text-slate-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">{doc.original_filename}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(doc.created_at).toLocaleDateString()} • {doc.status}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {doc.status === 'complete' && (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-slate-600">
                        {doc.normalized_finding_count || doc.raw_finding_count || 0} issues
                      </Badge>
                      {doc.confirmed_count > 0 && (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          {doc.confirmed_count} confirmed
                        </Badge>
                      )}
                    </div>
                  )}
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        loadDocument(doc.document_id);
                      }}>
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      {doc.status === 'uploaded' && (
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          startAnalysis(doc.document_id);
                        }}>
                          <Search className="w-4 h-4 mr-2" />
                          Analyze
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(doc.document_id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Render document review view
  const renderReviewView = () => {
    if (!selectedDocument) return null;
    
    const filteredFindings = getFilteredFindings();
    const stats = {
      total: filteredFindings.length,
      confirmed: filteredFindings.filter(f => f.reviewer_status === 'confirmed').length,
      dismissed: filteredFindings.filter(f => f.reviewer_status === 'dismissed').length,
      pending: filteredFindings.filter(f => f.reviewer_status === 'auto_flagged').length
    };
    
    return (
      <div className="flex flex-col h-[calc(100vh-120px)]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setView('list')}
              data-testid="back-to-list-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h2 className="font-semibold text-stone-900">{selectedDocument.original_filename}</h2>
              <p className="text-sm text-slate-500">
                {selectedDocument.page_count} page{selectedDocument.page_count !== 1 ? 's' : ''} • 
                Analyzed {selectedDocument.analyzed_at ? new Date(selectedDocument.analyzed_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {selectedDocument.status === 'complete' && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => startAnalysis(selectedDocument.document_id)}
                  disabled={analyzing}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${analyzing ? 'animate-spin' : ''}`} />
                  Re-analyze
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowExportModal(true)}
                  data-testid="export-btn"
                  disabled={!canExportPrintSend}
                  title={isDirty ? 'Apply changes to enable export' : ''}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handlePrint}
                  data-testid="print-btn"
                  disabled={!canExportPrintSend}
                  title={isDirty ? 'Apply changes to enable print' : ''}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleOpenSendModal}
                  data-testid="send-btn"
                  className={`${canExportPrintSend ? 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100' : ''}`}
                  disabled={!canExportPrintSend}
                  title={isDirty ? 'Apply changes to enable send' : ''}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Mail
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Deep Analysis Overlay */}
          <DeepAnalysisOverlay
            isVisible={analyzing}
            currentStage={analysisStage}
            progress={analysisProgress?.progress || 0}
          />
          
          {/* Document viewer */}
          <div className="flex-1 bg-slate-100 overflow-auto p-4">
            {selectedDocument.status !== 'complete' ? (
              <div className="h-full flex items-center justify-center">
                {analyzing ? (
                  <div className="text-center">
                    {/* Overlay handles the deep analysis display now */}
                    <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
                    <p className="font-medium text-stone-900">Deep Legal Analysis in progress...</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Quality review takes time. Please wait.
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <FileWarning className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="font-medium text-stone-900">Document not analyzed</p>
                    <Button 
                      onClick={() => startAnalysis(selectedDocument.document_id)} 
                      className="mt-4"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Start Deep Analysis
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative">
                {/* Toolbar */}
                <div className="flex items-center justify-between mb-4 bg-white rounded-lg p-2 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600 w-12 text-center">
                      {Math.round(zoom * 100)}%
                    </span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setZoom(z => Math.min(2, z + 0.1))}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Toggle for limiting on-page clutter */}
                    <Button
                      variant={showOnlyHighConfidence ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setShowOnlyHighConfidence(!showOnlyHighConfidence)}
                      className={showOnlyHighConfidence ? 'bg-slate-600' : ''}
                      title="Toggle showing only high-confidence findings on the document"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      {showOnlyHighConfidence ? 'Priority View' : 'All Visible'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => loadPreview(selectedDocument.document_id, currentPage - 1)}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => loadPreview(selectedDocument.document_id, currentPage + 1)}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <Button
                    variant={showAnnotations ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setShowAnnotations(!showAnnotations)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Annotations
                  </Button>
                  
                  {/* Edit Mode Toggle */}
                  {permissions?.can_edit_findings && (
                    <Button
                      variant={isEditMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setIsEditMode(!isEditMode)}
                      className={isEditMode ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                      data-testid="main-edit-mode-toggle"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      {isEditMode ? 'Editing' : 'Edit'}
                    </Button>
                  )}
                </div>

                {/* Markup Editor Toolbar - Floating draggable/resizable panel */}
                <FloatingToolbox isVisible={isEditMode}>
                  <MarkupEditorToolbar
                    activeTool={activeTool}
                    setActiveTool={setActiveTool}
                    canUndo={undoStack.length > 0}
                    canRedo={redoStack.length > 0}
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                    onResetToAI={handleResetToAI}
                    selectedAnnotation={selectedFinding}
                    onUpdateAnnotation={handleAnnotationUpdate}
                    onDeleteSelected={handleDeleteSelected}
                    isEditMode={isEditMode}
                    setIsEditMode={setIsEditMode}
                    tierPermissions={permissions}
                  />
                </FloatingToolbox>

                {/* Floating Apply Changes Button - appears when there are unapplied edits */}
                {isEditMode && isDirty && (
                  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
                    <Button
                      onClick={handleOpenReviewChanges}
                      className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg px-6 py-2 gap-2"
                      data-testid="apply-changes-btn"
                    >
                      <Save className="w-4 h-4" />
                      Apply Changes
                      <span className="ml-2 bg-amber-600 px-2 py-0.5 rounded text-xs">
                        {changesSummary.added + changesSummary.modified + changesSummary.deleted} edits
                      </span>
                    </Button>
                  </div>
                )}

                {/* Document image with annotations */}
                <div 
                  ref={documentContainerRef}
                  className="relative mx-auto bg-white shadow-lg"
                  style={{ 
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top center'
                  }}
                >
                  {previewImage ? (
                    <>
                      <img 
                        src={`data:image/png;base64,${previewImage}`}
                        alt="Document preview"
                        className="max-w-full"
                        onLoad={(e) => {
                          setPreviewDimensions({
                            width: e.target.naturalWidth,
                            height: e.target.naturalHeight
                          });
                        }}
                        data-testid="document-preview-image"
                      />
                      
                      {/* Interactive Annotation Overlay (Edit Mode) or Static Overlay (View Mode) */}
                      {isEditMode ? (
                        <InteractiveAnnotationOverlay
                          annotations={getCurrentAnnotations()}
                          pageWidth={selectedDocument?.page_layouts?.[currentPage - 1]?.width || 612}
                          pageHeight={selectedDocument?.page_layouts?.[currentPage - 1]?.height || 792}
                          renderWidth={previewDimensions.width}
                          renderHeight={previewDimensions.height}
                          zoom={zoom}
                          currentPage={currentPage}
                          selectedAnnotationId={selectedFinding?.id || selectedFinding?.finding_id || selectedFinding?.normalized_id}
                          highlightedFindingId={highlightedFindingId}
                          onAnnotationSelect={handleAnnotationSelect}
                          onAnnotationUpdate={handleAnnotationUpdate}
                          onAnnotationDelete={handleDeleteAnnotation}
                          showAnnotations={showAnnotations}
                          isEditMode={isEditMode}
                          activeTool={activeTool}
                          onCreateAnnotation={handleCreateAnnotation}
                        />
                      ) : (
                        <DocumentAnnotationOverlay
                          annotations={buildAnnotationsFromFindings()}
                          pageWidth={selectedDocument?.page_layouts?.[currentPage - 1]?.width || 612}
                          pageHeight={selectedDocument?.page_layouts?.[currentPage - 1]?.height || 792}
                          renderWidth={previewDimensions.width}
                          renderHeight={previewDimensions.height}
                          currentPage={currentPage}
                          selectedAnnotationId={selectedFinding?.finding_id || selectedFinding?.normalized_id}
                          highlightedFindingId={highlightedFindingId}
                          onAnnotationClick={handleAnnotationClick}
                          showAnnotations={showAnnotations}
                        />
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-96">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Findings panel */}
          <div className="w-[400px] border-l bg-white flex flex-col">
            {/* Panel header */}
            <div className="p-4 border-b">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-stone-900">Findings</h3>
                {permissions?.can_add_manual_findings && (
                  <Button variant="ghost" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                )}
              </div>
              
              {/* Stats */}
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-600">{stats.total} total</span>
                <span className="text-green-600">{stats.confirmed} confirmed</span>
                <span className="text-slate-400">{stats.dismissed} dismissed</span>
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2 mt-3">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-8 text-xs w-[110px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="auto_flagged">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Findings list */}
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {filteredFindings.length === 0 ? (
                <div className="text-center py-8">
                  <Scale className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No findings match filters</p>
                </div>
              ) : (
                filteredFindings.map((finding, idx) => {
                  const findingId = finding.finding_id || finding.normalized_id;
                  const isSelected = selectedFinding?.finding_id === finding.finding_id || 
                                    selectedFinding?.normalized_id === finding.normalized_id;
                  const category = finding.issue_type || finding.normalized_issue_type;
                  const severity = finding.severity || finding.normalized_severity;
                  const status = finding.reviewer_status;
                  const pageNum = finding.page_number || finding.coordinates?.page || 1;
                  const anchorType = finding.annotation_scope || finding.anchor_type || 'clause';
                  const isStructural = anchorType === 'structural' || finding.is_structural;
                  const supportingText = finding.detected_text_snippet || finding.supporting_snippet || finding.anchor_text || '';
                  
                  // Function to scroll and highlight this finding
                  const focusOnFinding = (e) => {
                    if (e) e.stopPropagation();
                    setSelectedFinding(finding);
                    
                    // Navigate to page if needed
                    if (finding.page_number && finding.page_number !== currentPage) {
                      loadPreview(selectedDocument.document_id, finding.page_number);
                    }
                    
                    // Scroll document to the finding's location
                    if (finding.coordinates && documentContainerRef.current) {
                      const scrollY = (finding.coordinates.y || 0) * (previewDimensions.height / (selectedDocument?.page_layouts?.[currentPage - 1]?.height || 792));
                      const container = documentContainerRef.current.closest('.overflow-auto');
                      if (container) {
                        container.scrollTo({
                          top: Math.max(0, scrollY - 150),
                          behavior: 'smooth'
                        });
                      }
                    }
                  };
                  
                  // Function to locate evidence on the document page with visual highlight
                  const locateOnPage = (e) => {
                    if (e) e.stopPropagation();
                    
                    // Set as selected finding
                    setSelectedFinding(finding);
                    
                    // Trigger highlight pulse effect
                    setHighlightedFindingId(findingId);
                    
                    // Clear highlight after animation completes
                    setTimeout(() => {
                      setHighlightedFindingId(null);
                    }, 2500);
                    
                    // Navigate to page if needed
                    const targetPage = finding.page_number || finding.coordinates?.page || 1;
                    if (targetPage !== currentPage) {
                      loadPreview(selectedDocument.document_id, targetPage);
                      // Wait for page to load before scrolling
                      setTimeout(() => {
                        scrollToEvidence();
                      }, 500);
                    } else {
                      scrollToEvidence();
                    }
                    
                    function scrollToEvidence() {
                      if (finding.coordinates && documentContainerRef.current) {
                        const pageHeight = selectedDocument?.page_layouts?.[targetPage - 1]?.height || 792;
                        const scaleY = previewDimensions.height / pageHeight;
                        
                        // Calculate center position of the evidence region
                        const evidenceY = (finding.coordinates.y || 0) * scaleY;
                        const evidenceHeight = (finding.coordinates.height || 20) * scaleY;
                        const centerY = evidenceY + (evidenceHeight / 2);
                        
                        const container = documentContainerRef.current.closest('.overflow-auto');
                        if (container) {
                          const containerHeight = container.clientHeight;
                          // Scroll so evidence is centered in the viewport
                          const scrollTarget = Math.max(0, centerY - (containerHeight / 2));
                          
                          container.scrollTo({
                            top: scrollTarget,
                            behavior: 'smooth'
                          });
                        }
                      }
                    }
                  };
                  
                  // Copy finding to clipboard - clean format for review use
                  const copyFindingToClipboard = async (e) => {
                    e.stopPropagation();
                    
                    // Build the issue title
                    const issueTitle = finding.short_label || finding.normalized_label || 'Issue';
                    
                    // Get the exact supporting text (document evidence)
                    const evidence = supportingText || '';
                    
                    // Build the law citation(s)
                    const lawRefs = finding.law_references || finding.merged_law_references || [];
                    const lawCitation = lawRefs.length > 0 
                      ? lawRefs.map(ref => ref.includes('§') ? ref : ref.replace('FDCPA ', 'FDCPA §')).join(', ')
                      : '';
                    
                    // Build the copy text in clean 3-line format
                    let copyText = issueTitle;
                    if (evidence) {
                      copyText += `\n"${evidence}"`;
                    }
                    if (lawCitation) {
                      copyText += `\n${lawCitation}`;
                    }
                    
                    try {
                      await navigator.clipboard.writeText(copyText);
                      toast.success('Copied to clipboard', { duration: 1500 });
                    } catch (err) {
                      // Fallback for older browsers
                      const textArea = document.createElement('textarea');
                      textArea.value = copyText;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      toast.success('Copied', { duration: 1500 });
                    }
                  };
                  
                  return (
                    <Card 
                      key={findingId || idx}
                      className={`cursor-pointer transition-all ${
                        isSelected ? 'ring-2 ring-red-500 bg-red-50/30' : 'hover:border-slate-300'
                      }`}
                      onClick={focusOnFinding}
                      data-testid={`finding-card-${idx}`}
                      data-finding-id={findingId}
                    >
                      <CardContent className="p-3">
                        {/* Page number and anchor type badges */}
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-medium">
                            Page {pageNum}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            isStructural 
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {isStructural ? 'Structural' : anchorType}
                          </span>
                          {isStructural && (
                            <span className="text-xs text-amber-600 italic">Layout-based</span>
                          )}
                        </div>
                        
                        {/* Issue title and severity with Copy button */}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="font-semibold text-sm text-stone-900 flex-1">
                            {finding.short_label || finding.normalized_label}
                          </span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                              onClick={copyFindingToClipboard}
                              title="Copy finding"
                              data-testid={`copy-finding-${idx}`}
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                            <Badge className={`text-xs ${SEVERITY_COLORS[severity] || SEVERITY_COLORS.medium}`}>
                              {severity}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* AI Explanation - clearly labeled */}
                        <div className="mb-2">
                          <div className="text-xs text-slate-500 font-medium mb-0.5">AI Analysis:</div>
                          <p className="text-xs text-slate-600 leading-relaxed">
                            {finding.explanation || finding.consolidated_explanation}
                          </p>
                        </div>
                        
                        {/* SUPPORTING TEXT - Exact snippet from the document */}
                        {supportingText && (
                          <div 
                            className="mb-2 p-2.5 bg-amber-50 border border-amber-300 rounded-md cursor-pointer hover:bg-amber-100 hover:border-amber-400 transition-all active:scale-[0.99]"
                            onClick={locateOnPage}
                            title="Click to locate this text on the document"
                          >
                            <div className="text-amber-800 font-semibold mb-1 flex items-center gap-1.5 text-xs">
                              <FileText className="w-3.5 h-3.5" />
                              Document Evidence:
                              <span className="ml-auto text-amber-600 text-xs font-medium hover:text-amber-700 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Click to locate
                              </span>
                            </div>
                            <p className="text-stone-800 italic leading-relaxed text-xs border-l-2 border-amber-400 pl-2 bg-white/50 py-1">
                              "{supportingText.slice(0, 250)}{supportingText.length > 250 ? '...' : ''}"
                            </p>
                          </div>
                        )}
                        
                        {/* Structural finding note */}
                        {isStructural && (
                          <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                            <div className="text-amber-700 font-medium flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Structural Finding
                            </div>
                            <p className="text-amber-600 text-xs mt-1">
                              This issue relates to document layout or visual presentation, not a specific clause.
                            </p>
                          </div>
                        )}
                        
                        {/* Category and status badges */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-xs">
                              {CATEGORY_LABELS[category] || category}
                            </Badge>
                            <Badge className={`text-xs ${STATUS_COLORS[status] || ''}`}>
                              {status?.replace('_', ' ')}
                            </Badge>
                          </div>
                          
                          {/* Action buttons */}
                          {status === 'auto_flagged' && (
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 text-green-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFindingAction(finding, 'confirm');
                                }}
                                title="Confirm"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 text-slate-400"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFindingAction(finding, 'dismiss');
                                }}
                                title="Dismiss"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                              {permissions?.can_edit_findings && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFindingAction(finding, 'edit');
                                  }}
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Law references */}
                        {(finding.law_references?.length > 0 || finding.merged_law_references?.length > 0) && (
                          <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-100">
                            <span className="text-xs text-slate-500 mr-1">Law:</span>
                            {(finding.law_references || finding.merged_law_references || []).map((ref, i) => (
                              <span key={i} className="text-xs px-1.5 py-0.5 bg-red-50 text-red-700 rounded font-medium">
                                {ref.includes('§') ? ref : ref.replace('FDCPA ', 'FDCPA §')}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Confidence indicator */}
                        {(finding.confidence_score || finding.normalized_confidence) && (
                          <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2">
                            <span className="text-xs text-slate-500">Confidence:</span>
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-emerald-500 rounded-full"
                                style={{ width: `${Math.round((finding.confidence_score || finding.normalized_confidence || 0.7) * 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-600 font-medium">
                              {Math.round((finding.confidence_score || finding.normalized_confidence || 0.7) * 100)}%
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className={view === 'list' ? 'max-w-5xl mx-auto p-6' : ''}>
        {view === 'list' && renderDocumentList()}
        {view === 'review' && renderReviewView()}
      </div>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Collection Letter</DialogTitle>
            <DialogDescription>
              Upload a PDF or image of a debt collection letter for AI-assisted compliance analysis.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                uploadFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              <input
                id="file-upload"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={handleFileSelect}
              />
              
              {uploadFile ? (
                <div>
                  <FileText className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
                  <p className="font-medium text-stone-900">{uploadFile.name}</p>
                  <p className="text-sm text-slate-500">
                    {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-600">Click to select or drag and drop</p>
                  <p className="text-sm text-slate-400 mt-1">PDF, PNG, JPG up to 20MB</p>
                </div>
              )}
            </div>

            <div className="bg-amber-50 rounded-lg p-3 text-sm">
              <p className="text-amber-800">
                <strong>Important:</strong> This tool identifies potential compliance issues. 
                AI suggestions require reviewer confirmation - they are not final legal conclusions.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!uploadFile || uploading}
              data-testid="confirm-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Analyze
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export Analysis</DialogTitle>
            <DialogDescription>
              Choose an export format for your analysis results.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-3">
            <Button 
              variant="outline" 
              className="justify-start h-auto p-4"
              onClick={() => handleExport('json')}
            >
              <div className="text-left">
                <p className="font-medium">JSON Export</p>
                <p className="text-sm text-slate-500">Structured data for integration</p>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start h-auto p-4"
              onClick={() => handleExport('summary')}
            >
              <div className="text-left">
                <p className="font-medium">Issue Summary</p>
                <p className="text-sm text-slate-500">Text summary of all findings</p>
              </div>
            </Button>
            
            {permissions?.can_export_pdf && (
              <Button 
                variant="outline" 
                className="justify-start h-auto p-4"
                onClick={() => handleExport('pdf')}
              >
                <div className="text-left">
                  <p className="font-medium">Annotated PDF</p>
                  <p className="text-sm text-slate-500">Document with markup overlays</p>
                </div>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Finding Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Finding</DialogTitle>
          </DialogHeader>
          
          {editingFinding && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Label</label>
                <Input
                  value={editingFinding.short_label || ''}
                  onChange={(e) => setEditingFinding({
                    ...editingFinding,
                    short_label: e.target.value
                  })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Explanation</label>
                <Textarea
                  value={editingFinding.explanation || ''}
                  onChange={(e) => setEditingFinding({
                    ...editingFinding,
                    explanation: e.target.value
                  })}
                  className="mt-1"
                  rows={4}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Severity</label>
                <Select 
                  value={editingFinding.severity || 'medium'} 
                  onValueChange={(val) => setEditingFinding({
                    ...editingFinding,
                    severity: val
                  })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Reviewer Notes</label>
                <Textarea
                  value={editingFinding.reviewer_notes || ''}
                  onChange={(e) => setEditingFinding({
                    ...editingFinding,
                    reviewer_notes: e.target.value
                  })}
                  className="mt-1"
                  rows={2}
                  placeholder="Add notes about this finding..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analysis Progress Overlay */}
      {analyzing && view === 'list' && (
        <div className="fixed inset-0 bg-[rgba(6,28,20,0.75)] flex items-center justify-center z-50">
          <Card className="w-96">
            <CardContent className="p-6 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
              <p className="font-medium text-stone-900">Analyzing document...</p>
              <p className="text-sm text-slate-500 mt-1 capitalize">
                {analysisProgress?.stage?.replace(/_/g, ' ') || 'Processing'}
              </p>
              <div className="w-full bg-slate-200 rounded-full h-2 mt-4">
                <div 
                  className="bg-emerald-600 h-2 rounded-full transition-all"
                  style={{ width: `${analysisProgress?.progress || 0}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {analysisProgress?.progress || 0}% complete
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send Modal - Postal Mail via PostGrid */}
      <Dialog open={showSendModal} onOpenChange={(open) => !open && handleCloseSendModal()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-teal-600" />
              Send Mail to Debt Collector
            </DialogTitle>
            <DialogDescription>
              Send the reviewed document via postal mail through our mail integration.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                sendState.step === 'prepare' 
                  ? 'bg-teal-100 text-teal-700' 
                  : 'bg-teal-500 text-white'
              }`}>1</div>
              <div className="w-8 h-0.5 bg-slate-200" />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                sendState.step === 'validating' 
                  ? 'bg-teal-100 text-teal-700' 
                  : sendState.step === 'review' || sendState.step === 'sending' || sendState.step === 'complete'
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-200 text-slate-500'
              }`}>2</div>
              <div className="w-8 h-0.5 bg-slate-200" />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                sendState.step === 'sending' 
                  ? 'bg-teal-100 text-teal-700' 
                  : sendState.step === 'complete'
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-200 text-slate-500'
              }`}>3</div>
            </div>
            
            {/* Step 1: Enter Mailing Address */}
            {sendState.step === 'prepare' && (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-emerald-800 mb-1 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Postal Mail Delivery
                  </p>
                  <p className="text-emerald-700">
                    The reviewed document will be printed and mailed to the debt collector via USPS.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-700 flex items-center gap-2">
                    Recipient Mailing Address
                    {sendState.recipient?.extracted_from_letter && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">AI Suggested</span>
                    )}
                  </h4>
                  
                  <div>
                    <label className="text-xs text-slate-500">Company/Collector Name</label>
                    <Input
                      value={sendState.recipient?.company_name || sendState.recipient?.collector_name || ''}
                      onChange={(e) => setSendState(prev => ({
                        ...prev,
                        recipient: { ...prev.recipient, company_name: e.target.value, collector_name: e.target.value, manually_edited: true }
                      }))}
                      placeholder="Credit Control, LLC"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-slate-500">Street Address *</label>
                    <Input
                      value={sendState.recipient?.address_line1 || ''}
                      onChange={(e) => setSendState(prev => ({
                        ...prev,
                        recipient: { ...prev.recipient, address_line1: e.target.value, manually_edited: true }
                      }))}
                      placeholder="123 Main Street"
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-slate-500">Address Line 2 (Suite, Apt, etc.)</label>
                    <Input
                      value={sendState.recipient?.address_line2 || ''}
                      onChange={(e) => setSendState(prev => ({
                        ...prev,
                        recipient: { ...prev.recipient, address_line2: e.target.value, manually_edited: true }
                      }))}
                      placeholder="Suite 100"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-6 gap-2">
                    <div className="col-span-3">
                      <label className="text-xs text-slate-500">City *</label>
                      <Input
                        value={sendState.recipient?.city || ''}
                        onChange={(e) => setSendState(prev => ({
                          ...prev,
                          recipient: { ...prev.recipient, city: e.target.value, manually_edited: true }
                        }))}
                        placeholder="City"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="text-xs text-slate-500">State *</label>
                      <Input
                        value={sendState.recipient?.state || ''}
                        onChange={(e) => setSendState(prev => ({
                          ...prev,
                          recipient: { ...prev.recipient, state: e.target.value, manually_edited: true }
                        }))}
                        placeholder="CA"
                        className="mt-1"
                        maxLength={2}
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-slate-500">ZIP Code *</label>
                      <Input
                        value={sendState.recipient?.postal_code || ''}
                        onChange={(e) => setSendState(prev => ({
                          ...prev,
                          recipient: { ...prev.recipient, postal_code: e.target.value, manually_edited: true }
                        }))}
                        placeholder="90210"
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={handleCloseSendModal}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleValidateAddress}
                    disabled={!sendState.recipient?.address_line1 || !sendState.recipient?.city}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Validate & Continue
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 1.5: Validating */}
            {sendState.step === 'validating' && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-4" />
                <p className="text-slate-600">Validating address...</p>
              </div>
            )}
            
            {/* Step 2: Review & Confirm */}
            {sendState.step === 'review' && (
              <div className="space-y-4">
                {sendState.addressValidated ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-green-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Address Validated
                    </p>
                    <p className="text-green-700 text-xs mt-1">
                      Address has been verified and standardized.
                    </p>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Address Not Verified
                    </p>
                    <p className="text-amber-700 text-xs mt-1">
                      {sendState.error || 'Please verify the mailing address before sending.'}
                    </p>
                  </div>
                )}
                
                <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-2">
                  <h4 className="font-medium text-slate-700">Mailing To:</h4>
                  <div className="text-slate-600">
                    <p className="font-medium">{sendState.recipient?.company_name || sendState.recipient?.collector_name || 'Recipient'}</p>
                    <p>{sendState.recipient?.address_line1}</p>
                    {sendState.recipient?.address_line2 && <p>{sendState.recipient?.address_line2}</p>}
                    <p>{sendState.recipient?.city}, {sendState.recipient?.state} {sendState.recipient?.postal_code}</p>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <h4 className="font-medium text-slate-700 mb-2">Document to Mail</h4>
                  <p className="text-slate-600 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Reviewed Collection Letter (Snapshot v{latestSnapshot?.version_number || 1})
                  </p>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setSendState(prev => ({ ...prev, step: 'prepare' }))}>
                    Edit Address
                  </Button>
                  <Button 
                    onClick={handleExecutePostalSend}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Submit Mail Job
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 3: Sending */}
            {sendState.step === 'sending' && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-4" />
                <p className="text-slate-600">Submitting mail job...</p>
              </div>
            )}
            
            {/* Step 4: Complete */}
            {sendState.step === 'complete' && (
              <div className="space-y-4">
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-medium text-lg text-stone-800">Mail Job Submitted</h3>
                  <p className="text-sm text-slate-500 text-center mt-2">
                    The document will be printed and mailed to the debt collector.
                  </p>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <h4 className="font-medium text-slate-700 mb-2">Audit Trail</h4>
                  <div className="space-y-1 text-slate-600">
                    <p><span className="text-slate-500">Send ID:</span> {sendState.sendId?.slice(0, 8)}...</p>
                    <p><span className="text-slate-500">PostGrid ID:</span> {sendState.postgridResult?.letter_id || 'Pending'}</p>
                    <p><span className="text-slate-500">Snapshot:</span> v{latestSnapshot?.version_number || 1}</p>
                    <p><span className="text-slate-500">Mailed To:</span> {sendState.recipient?.city}, {sendState.recipient?.state}</p>
                    <p><span className="text-slate-500">Status:</span> <span className="text-green-600 font-medium">{sendState.postgridResult?.demo_mode ? 'Demo Mode' : 'Submitted'}</span></p>
                  </div>
                </div>
                
                <div className="flex justify-end pt-4">
                  <Button onClick={handleCloseSendModal} className="bg-teal-600 hover:bg-teal-700">
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Changes Modal */}
      <Dialog open={showReviewChangesModal} onOpenChange={setShowReviewChangesModal}>
        <DialogContent className="sm:max-w-xl p-8">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-amber-600" />
              Review Changes
            </DialogTitle>
            <DialogDescription>
              Review your markup changes before applying them.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-amber-800 mb-3">Changes Summary</p>
              <div className="grid grid-cols-2 gap-3">
                {changesSummary.added > 0 && (
                  <div className="flex items-center gap-2 text-green-700">
                    <Plus className="w-4 h-4" />
                    <span>{changesSummary.added} added</span>
                  </div>
                )}
                {changesSummary.modified > 0 && (
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Edit className="w-4 h-4" />
                    <span>{changesSummary.modified} modified</span>
                  </div>
                )}
                {changesSummary.deleted > 0 && (
                  <div className="flex items-center gap-2 text-red-700">
                    <Trash2 className="w-4 h-4" />
                    <span>{changesSummary.deleted} deleted</span>
                  </div>
                )}
                {changesSummary.added === 0 && changesSummary.modified === 0 && changesSummary.deleted === 0 && (
                  <div className="col-span-2 text-slate-500">
                    No changes detected
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-4 text-sm">
              <p className="font-medium text-slate-700 mb-2">After applying changes:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                <li>A new snapshot version will be created</li>
                <li>Export, Print, and Send will use this snapshot</li>
                <li>Your original document remains unchanged</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter className="flex-row justify-between gap-3 pt-2">
            <Button variant="outline" onClick={handleDiscardChanges} className="text-red-600 hover:text-red-700 shrink-0">
              Discard Changes
            </Button>
            <div className="flex gap-3 shrink-0">
              <Button variant="outline" onClick={() => setShowReviewChangesModal(false)} className="shrink-0">
                Continue Editing
              </Button>
              <Button 
                onClick={handleApplyChanges}
                disabled={isApplying}
                className="bg-amber-500 hover:bg-amber-600 shrink-0 px-5"
              >
                {isApplying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Apply Changes
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectionLetterMarkupPage;
